import React, { useEffect, useState } from 'react'
import { useNavigate, useSearchParams, Link } from 'react-router-dom'
import { motion } from 'framer-motion'
import { supabase } from '@/lib/supabase'
import { Zap, ArrowRight, Loader2 } from 'lucide-react'

/**
 * Invite acceptance page.
 *
 * URL: /auth/accept-invite?token=...
 *
 * Flow:
 *   1. Read token from query param.
 *   2. Call accept-invite (op: 'lookup') to validate the token and fetch
 *      the inviter / workspace / role / email. Edge function is deployed
 *      with --no-verify-jwt, so it accepts unauthenticated callers — the
 *      bearer credential is the token itself.
 *   3. On success: render a form for full name + password.
 *   4. On submit: call accept-invite (op: 'accept'); on success, sign in
 *      via supabase.auth.signInWithPassword and navigate to /app.
 *
 * The accept-invite function handles all the validation paths (invalid
 * token, expired, already accepted, email already attached to another
 * workspace); this page just renders whichever state the function reports.
 */

const FUNCTIONS_URL = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/accept-invite`

interface InviteLookup {
  email: string
  role: 'admin' | 'agent' | 'viewer'
  workspaceName: string
  inviterName: string
  expiresAt: string
}

type PageState =
  | { kind: 'loading' }
  | { kind: 'error'; message: string }
  | { kind: 'ready'; invite: InviteLookup }
  | { kind: 'accepting'; invite: InviteLookup }

export default function AcceptInvite() {
  const navigate = useNavigate()
  const [params] = useSearchParams()
  const token = params.get('token') || ''

  const [state, setState] = useState<PageState>({ kind: 'loading' })
  const [fullName, setFullName] = useState('')
  const [password, setPassword] = useState('')
  const [submitError, setSubmitError] = useState('')

  useEffect(() => {
    if (!token) {
      setState({ kind: 'error', message: 'No invite token in the URL.' })
      return
    }
    let cancelled = false
    ;(async () => {
      try {
        const res = await fetch(FUNCTIONS_URL, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            // Supabase Functions gateway requires the anon key even when
            // --no-verify-jwt is set (it identifies the project).
            'apikey': import.meta.env.VITE_SUPABASE_ANON_KEY as string,
          },
          body: JSON.stringify({ op: 'lookup', token }),
        })
        const json = await res.json().catch(() => ({}))
        if (cancelled) return
        if (!res.ok) {
          setState({ kind: 'error', message: json.error || `Lookup failed (${res.status})` })
          return
        }
        setState({ kind: 'ready', invite: json as InviteLookup })
      } catch (e: any) {
        if (!cancelled) setState({ kind: 'error', message: e?.message || 'Network error' })
      }
    })()
    return () => { cancelled = true }
  }, [token])

  const handleAccept = async (e: React.FormEvent) => {
    e.preventDefault()
    if (state.kind !== 'ready') return
    const invite = state.invite  // captured for error-recovery
    setSubmitError('')
    if (!fullName.trim()) { setSubmitError('Enter your full name'); return }
    if (password.length < 8) { setSubmitError('Password must be at least 8 characters'); return }

    setState({ kind: 'accepting', invite })
    try {
      const res = await fetch(FUNCTIONS_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'apikey': import.meta.env.VITE_SUPABASE_ANON_KEY as string,
        },
        body: JSON.stringify({ op: 'accept', token, password, fullName: fullName.trim() }),
      })
      const json = await res.json().catch(() => ({}))
      if (!res.ok) {
        setSubmitError(json.error || `Accept failed (${res.status})`)
        setState({ kind: 'ready', invite })
        return
      }
      // Sign in immediately with the password we just collected.
      const email = (json as { email: string }).email
      const { error: signInErr } = await supabase.auth.signInWithPassword({ email, password })
      if (signInErr) {
        setSubmitError(`Account created but sign-in failed: ${signInErr.message}. Try signing in manually.`)
        setState({ kind: 'ready', invite })
        return
      }
      navigate('/app')
    } catch (err: any) {
      setSubmitError(err?.message || 'Network error')
      setState({ kind: 'ready', invite })
    }
  }

  const inputStyle: React.CSSProperties = {
    width: '100%',
    padding: '11px 14px',
    background: 'var(--hover-bg)',
    border: '1px solid var(--border-default)',
    borderRadius: 10,
    fontSize: 14,
    color: 'var(--ink-primary)',
    outline: 'none',
    boxSizing: 'border-box',
    transition: 'border-color 150ms',
  }

  const labelStyle: React.CSSProperties = {
    fontSize: 12,
    fontWeight: 600,
    color: 'var(--ink-tertiary)',
    display: 'block',
    marginBottom: 7,
    letterSpacing: '0.02em',
  }

  return (
    <div style={{ minHeight: '100vh', background: 'var(--canvas)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24, position: 'relative', overflow: 'hidden' }}>
      <div style={{ position: 'absolute', top: '20%', right: '25%', width: 500, height: 500, background: 'radial-gradient(circle, color-mix(in srgb, var(--accent) var(--status-tint-faint), transparent) 0%, transparent 70%)', pointerEvents: 'none' }} />

      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }} style={{ width: '100%', maxWidth: 420, position: 'relative' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 28, justifyContent: 'center' }}>
          <div style={{ width: 36, height: 36, borderRadius: 10, background: 'var(--logo-bg-gradient)', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: 'var(--logo-glow)' }}>
            <Zap style={{ width: 18, height: 18, color: 'var(--accent-ink)' }} />
          </div>
          <span style={{ fontSize: 18, fontWeight: 800, color: 'var(--ink-primary)', letterSpacing: '-0.02em' }}>Orkestra</span>
        </div>

        <div style={{ background: 'var(--surface)', border: '1px solid var(--border-subtle)', borderRadius: 16, padding: 32 }}>
          {state.kind === 'loading' && (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12, padding: '20px 0' }}>
              <Loader2 style={{ width: 22, height: 22, color: 'var(--ink-tertiary)' }} className="animate-spin" />
              <p style={{ fontSize: 13, color: 'var(--ink-tertiary)', margin: 0 }}>Validating your invite…</p>
            </div>
          )}

          {state.kind === 'error' && (
            <div>
              <h2 style={{ fontSize: 20, fontWeight: 800, color: 'var(--ink-primary)', margin: '0 0 8px', letterSpacing: '-0.02em' }}>Invite unavailable</h2>
              <p style={{ fontSize: 14, color: 'var(--ink-secondary)', margin: '0 0 20px', lineHeight: 1.55 }}>{state.message}</p>
              <Link to="/signin" style={{ display: 'inline-flex', alignItems: 'center', gap: 6, color: 'var(--accent-text)', fontSize: 13, fontWeight: 600, textDecoration: 'none' }}>
                Go to sign in <ArrowRight style={{ width: 13, height: 13 }} />
              </Link>
            </div>
          )}

          {(state.kind === 'ready' || state.kind === 'accepting') && (() => {
            const invite = state.invite
            const accepting = state.kind === 'accepting'
            return (
              <>
                <h2 style={{ fontSize: 20, fontWeight: 800, color: 'var(--ink-primary)', margin: '0 0 6px', letterSpacing: '-0.02em' }}>
                  Join {invite.workspaceName}
                </h2>
                <p style={{ fontSize: 13, color: 'var(--ink-secondary)', margin: '0 0 24px', lineHeight: 1.55 }}>
                  <strong>{invite.inviterName}</strong> invited you as a <strong style={{ textTransform: 'capitalize' }}>{invite.role}</strong>. Set up your account to accept.
                </p>

                <form onSubmit={handleAccept} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                  <div>
                    <label style={labelStyle}>Email</label>
                    <input
                      type="email"
                      value={invite.email}
                      disabled
                      style={{ ...inputStyle, color: 'var(--ink-tertiary)', cursor: 'not-allowed' }}
                    />
                  </div>
                  <div>
                    <label style={labelStyle}>Your name</label>
                    <input
                      type="text"
                      value={fullName}
                      onChange={e => setFullName(e.target.value)}
                      placeholder="Jane Doe"
                      autoComplete="name"
                      autoFocus
                      style={inputStyle}
                      onFocus={e => e.currentTarget.style.borderColor = 'var(--focus-ring)'}
                      onBlur={e => e.currentTarget.style.borderColor = 'var(--border-default)'}
                    />
                  </div>
                  <div>
                    <label style={labelStyle}>Password</label>
                    <input
                      type="password"
                      value={password}
                      onChange={e => setPassword(e.target.value)}
                      placeholder="At least 8 characters"
                      autoComplete="new-password"
                      minLength={8}
                      style={inputStyle}
                      onFocus={e => e.currentTarget.style.borderColor = 'var(--focus-ring)'}
                      onBlur={e => e.currentTarget.style.borderColor = 'var(--border-default)'}
                    />
                  </div>

                  {submitError && (
                    <p style={{ fontSize: 12, color: 'var(--danger)', margin: 0 }}>{submitError}</p>
                  )}

                  <button
                    type="submit"
                    disabled={accepting}
                    className="btn-primary"
                    style={{ justifyContent: 'center', marginTop: 6 }}
                  >
                    {accepting ? (
                      <>
                        <Loader2 style={{ width: 14, height: 14 }} className="animate-spin" />
                        Accepting…
                      </>
                    ) : (
                      <>
                        Accept invite <ArrowRight style={{ width: 14, height: 14 }} />
                      </>
                    )}
                  </button>
                </form>
              </>
            )
          })()}
        </div>
      </motion.div>
    </div>
  )
}
